/**
 * PROFILES - Sistema Multi-Rol
 * 
 * Permite que un usuario tenga múltiples perfiles/roles:
 * - Paciente
 * - Terapeuta
 * - Admin de Escuela
 * - Gestor de Espacios
 */

import { Address } from './common';
import { Permission } from './enums';

// ============================================
// PROFILE TYPES
// ============================================

export enum ProfileType {
  PATIENT = 'patient',
  THERAPIST = 'therapist',
  SCHOOL = 'school',
  SCHOOL_ADMIN = 'school_admin',
  SCHOOL_EDITOR = 'school_editor',
  SPACE_MANAGER = 'space_manager',
  PLATFORM_ADMIN = 'platform_admin',
  PLATFORM_CURATOR = 'platform_curator'
}

// ============================================
// USER WITH PROFILES
// ============================================

export interface User {
  id: string;
  email: string;
  displayName: string;
  avatar?: string;
  
  // Multi-rol
  activeProfiles: string[]; // IDs de perfiles
  currentProfileId: string; // Perfil activo
  
  // Settings globales
  preferences: UserPreferences;
  
  // Metadata
  createdAt: string;
  lastLogin: string;
  isActive: boolean;
}

export interface UserPreferences {
  language: 'es' | 'en';
  timezone: string;
  notifications: NotificationSettings;
  theme: 'light' | 'dark';
}

export interface NotificationSettings {
  email: boolean;
  push: boolean;
  sms: boolean;
  frequency: 'realtime' | 'daily' | 'weekly';
}

// ============================================
// BASE PROFILE
// ============================================

export interface UserProfile {
  id: string;
  uid?: string;
  userId: string;
  type: ProfileType;

  // Common optional fields (denormalized for convenience)
  name?: string;
  schoolId?: string;

  // Datos específicos según tipo
  data: ProfileData;

  // Status
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProfileDataBase {
  name?: string;
  schoolId?: string;
}

export type ProfileData =
  | PatientProfileData
  | TherapistProfileData
  | SchoolAdminProfileData
  | SchoolEditorProfileData
  | SpaceManagerProfileData;

// ============================================
// PATIENT PROFILE
// ============================================

export interface PatientProfileData extends ProfileDataBase {
  name: string;
  phone?: string;
  emergencyContact?: EmergencyContact;
  
  // Relación terapéutica
  primaryTherapistId?: string;
  
  // Datos clínicos (privados)
  diagnosis?: string;
  medications?: string[];
  allergies?: string[];
  
  // Journeys asignados
  activeJourneys: string[];
  completedJourneys: string[];
  
  // Preferencias
  sessionPreferences: {
    preferredDays: number[];
    preferredTimes: string[];
    inPerson: boolean;
    online: boolean;
  };
}

export interface EmergencyContact {
  name: string;
  phone: string;
  relationship: string;
}

// ============================================
// THERAPIST PROFILE
// ============================================

export interface TherapistProfileData extends ProfileDataBase {
  // Info profesional
  license: string;
  title: string; // Dr., Lic., etc.
  specialties: string[];
  approaches: string[];
  yearsExperience: number;
  bio: string;
  
  // Certificaciones
  certifications: Certification[];
  
  // Asociaciones a escuelas
  schoolAffiliations: SchoolAffiliation[];
  
  // Settings de práctica
  sessionPrice: number;
  firstSessionFree: boolean;
  acceptsInsurance: boolean;
  acceptingNewPatients: boolean;
  
  // Subscripción en Appmor
  subscriptionTier: 'free' | 'pro' | 'premium';
  marketplaceAccess: string[]; // IDs de programas comprados
  
  // Espacios
  preferredSpaces: string[]; // IDs de salas favoritas
  ownedSpaces: string[]; // Salas propias
  
  // Stats
  totalPatients: number;
  activePatients: number;
  totalSessions: number;
  averageRating: number;
  
  // Metadata
  onboardingCompleted: boolean;
  isPublic: boolean;
}

export interface Certification {
  name: string;
  issuedBy: string;
  issuedDate: string;
  expiryDate?: string;
  certificateUrl?: string;
}

export interface SchoolAffiliation {
  schoolId: string;
  role: 'founder' | 'instructor' | 'affiliated';
  permissions: string[];
  joinedAt: string;
  isActive: boolean;
}

// ============================================
// SCHOOL ADMIN PROFILE
// ============================================

export interface SchoolAdminProfileData extends ProfileDataBase {
  schoolId: string;
  role: 'owner' | 'admin' | 'manager';
  permissions: Permission[];
  
  // Si también es terapeuta (caso Institut AMAR)
  therapistProfileId?: string;
  
  // Responsabilidades
  canManageContent: boolean;
  canManageMembers: boolean;
  canManageSpaces: boolean;
  canViewAnalytics: boolean;
  canManageFinances: boolean;
  
  joinedAt: string;
}

// ============================================
// SCHOOL EDITOR PROFILE
// ============================================

export interface SchoolEditorProfileData extends ProfileDataBase {
  schoolId: string;
  permissions: Permission[];
  
  // Límites de edición
  canPublish: boolean;
  canDeleteContent: boolean;
  
  // Stats
  practicesCreated: number;
  programsCreated: number;
  
  joinedAt: string;
}

// ============================================
// SPACE MANAGER PROFILE
// ============================================

export interface SpaceManagerProfileData extends ProfileDataBase {
  // Espacios que gestiona (propios o de escuela)
  managedSpaces: string[];
  
  // Si también es terapeuta
  therapistProfileId?: string;
  usedSpaces?: string[]; // Salas que usa como terapeuta
  
  // Si también es escuela
  schoolId?: string;
  offeredSpaces?: string[]; // Salas ofrecidas por la escuela
  
  // Stats
  totalBookings: number;
  totalRevenue: number;
  averageRating: number;
}

// ============================================
// HELPER TYPES
// ============================================

export interface ProfilePermissions {
  content: {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
    publish: boolean;
  };
  members: {
    invite: boolean;
    remove: boolean;
    updatePermissions: boolean;
  };
  spaces: {
    create: boolean;
    update: boolean;
    delete: boolean;
    manageBookings: boolean;
  };
  analytics: {
    viewAll: boolean;
    export: boolean;
  };
  finances: {
    viewRevenue: boolean;
    managePricing: boolean;
    viewPayments: boolean;
  };
}

